import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type MediaEntry = {
  url: string;
  blob: Blob;
};

interface EphemeralState {
  audio: MediaEntry | null;
  video: MediaEntry | null;
}

interface EphemeralActions {
  setAudio: (blob: Blob) => void;
  setVideo: (blob: Blob) => void;
  clearAudio: () => void;
  clearVideo: () => void;
  clearAll: () => void;
}

export type EphemeralStore = EphemeralState & EphemeralActions;

/**
 * UNPERSISTED store — intentionally has no `persist` middleware.
 * Survives same-document React Router navigations (component unmount/remount)
 * but is cleared on a hard reload, at which point useBlobUrl falls back to
 * IndexedDB.
 *
 * Object URLs are created here (once per blob) and revoked here (on replace or
 * clear) so there is a single owner responsible for their lifecycle.
 */
const useEphemeralStore = create<EphemeralStore>()(
  devtools(
    (set, get) => ({
      audio: null,
      video: null,

      setAudio: (blob: Blob) => {
        // Revoke the previous URL before replacing to avoid memory leaks
        const existing = get().audio;
        if (existing) URL.revokeObjectURL(existing.url);
        set({ audio: { blob, url: URL.createObjectURL(blob) } }, false, 'setAudio');
      },

      setVideo: (blob: Blob) => {
        const existing = get().video;
        if (existing) URL.revokeObjectURL(existing.url);
        set({ video: { blob, url: URL.createObjectURL(blob) } }, false, 'setVideo');
      },

      clearAudio: () => {
        const existing = get().audio;
        if (existing) URL.revokeObjectURL(existing.url);
        set({ audio: null }, false, 'clearAudio');
      },

      clearVideo: () => {
        const existing = get().video;
        if (existing) URL.revokeObjectURL(existing.url);
        set({ video: null }, false, 'clearVideo');
      },

      clearAll: () => {
        const { audio, video } = get();
        if (audio) URL.revokeObjectURL(audio.url);
        if (video) URL.revokeObjectURL(video.url);
        set({ audio: null, video: null }, false, 'clearAll');
      },
    }),
    { name: 'ephemeral-store' },
  ),
);

// Hooks

export const useEphemeralAudio = () => useEphemeralStore((s) => s.audio);
export const useEphemeralVideo = () => useEphemeralStore((s) => s.video);
export const useSetEphemeralAudio = () => useEphemeralStore((s) => s.setAudio);
export const useSetEphemeralVideo = () => useEphemeralStore((s) => s.setVideo);
export const useClearEphemeralAudio = () => useEphemeralStore((s) => s.clearAudio);
export const useClearEphemeralVideo = () => useEphemeralStore((s) => s.clearVideo);
export const useClearAllEphemeral = () => useEphemeralStore((s) => s.clearAll);

// Outside-component access

export const getEphemeralAudio = () => useEphemeralStore.getState().audio;
export const getEphemeralVideo = () => useEphemeralStore.getState().video;

export default useEphemeralStore;